/**
 * Sentry initialisation for the CleanBid frontend.
 *
 * Pure infrastructure module. Never throws. No-ops cleanly when
 * REACT_APP_SENTRY_DSN is unset so local + Sentry-less builds behave
 * identically to pre-Sprint-240 code.
 *
 * Imported and called once from `src/index.js` BEFORE React renders.
 */
import * as Sentry from '@sentry/react';

// Header names whose values must be redacted from breadcrumbs.
const SENSITIVE_HEADER_KEYS = new Set([
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'stripe-signature',
  'telnyx-signature',
  'x-twilio-signature',
  'x-firebase-token',
]);

const FILTERED = '[FILTERED]';

function scrubHeaders(headers) {
  if (!headers || typeof headers !== 'object') return headers;
  const out = {};
  for (const [k, v] of Object.entries(headers)) {
    out[k] = SENSITIVE_HEADER_KEYS.has(String(k).toLowerCase()) ? FILTERED : v;
  }
  return out;
}

function readFloat(name, fallback) {
  const raw = process.env[name];
  if (!raw) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Initialise Sentry. Returns true if the SDK was activated.
 *
 * Safe no-op when REACT_APP_SENTRY_DSN is empty/undefined — no SDK
 * calls happen, no listeners attach, and the rest of the app proceeds
 * exactly as before.
 */
export function initSentry() {
  const dsn = (process.env.REACT_APP_SENTRY_DSN || '').trim();
  if (!dsn) {
    // Loud-enough log for ops, quiet enough not to alarm regular users.
    if (typeof console !== 'undefined') {
      console.info('[Sentry] DSN not set — running without error monitoring');
    }
    return false;
  }

  const environment =
    (process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development').trim() || 'development';
  const release = (process.env.REACT_APP_SENTRY_RELEASE || '').trim() || undefined;

  Sentry.init({
    dsn,
    environment,
    release,
    // PII posture mirrors the backend: never auto-collect.
    sendDefaultPii: false,
    // Tracing — conservative default; raise via env per environment.
    tracesSampleRate: readFloat('REACT_APP_SENTRY_TRACES_SAMPLE_RATE', 0.1),
    // Session replay: off by default, but always record sessions that
    // contained an error (the highest-value replays for debugging).
    replaysSessionSampleRate: readFloat(
      'REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
      0.0,
    ),
    replaysOnErrorSampleRate: readFloat(
      'REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE',
      1.0,
    ),
    integrations: [
      Sentry.replayIntegration({
        // Defense-in-depth: mask all text + media in replays so customer
        // PII, billing details, and phone numbers never leave the browser.
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserTracingIntegration(),
    ],
    beforeSend(event) {
      // Strip auth-bearing headers from the request snapshot if present.
      const req = event?.request;
      if (req && req.headers) {
        req.headers = scrubHeaders(req.headers);
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Fetch/XHR breadcrumbs sometimes carry header dumps. Scrub
      // before they are stored.
      if (
        (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
        breadcrumb.data &&
        breadcrumb.data.headers
      ) {
        breadcrumb.data.headers = scrubHeaders(breadcrumb.data.headers);
      }
      return breadcrumb;
    },
  });

  console.info(
    `[Sentry] initialised · env=${environment} · release=${release || '(none)'}`,
  );
  return true;
}

/**
 * Attach the authenticated user's id + companyId to subsequent events.
 * id only — never email/name — to keep PII out of Sentry by default.
 * Safe to call even when Sentry is uninitialised (Sentry's setUser is
 * a documented no-op in that state).
 */
export function setSentryUser({ uid, companyId } = {}) {
  if (uid) {
    Sentry.setUser({ id: uid });
  }
  if (companyId) {
    Sentry.setTag('companyId', companyId);
  }
}

/** Clear user context on sign-out. */
export function clearSentryUser() {
  Sentry.setUser(null);
}

// Re-export ErrorBoundary so the app imports a single module rather than
// pulling @sentry/react directly from every consumer — keeps the dependency
// boundary tight.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
